import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GlobalStyles } from '@mui/material';
import {
	Box,
	Sidebar,
	SidebarHeader,
	SidebarContent,
	SidebarMenu,
	SidebarMenuItem,
	SidebarMenuButton,
	SidebarMenuSub,
	SidebarMenuSubItem,
} from '@dextech-ai/dex-design-system';

import { ReactComponent as IconParticipants } from '@/assets/svgs/Participants.svg';
import { ReactComponent as IconDataDictionary } from '@/assets/svgs/SelectionYes.svg';
import { ReactComponent as IconUserManagement } from '@/assets/svgs/User-Management.svg';
import { ReactComponent as IconActivity } from '@/assets/svgs/icon-activity.svg';
import { ReactComponent as IconApprovalRequests } from '@/assets/svgs/icon-approval-requests-nav.svg';
import { ReactComponent as IconDashboard } from '@/assets/svgs/icon-dashboard.svg';
import { ReactComponent as IconDataExchange } from '@/assets/svgs/icon-ds-data-element.svg';
import { ReactComponent as IconRelationShipSetup } from '@/assets/svgs/icon-ds-relation-status.svg';
import { ReactComponent as IconServiceProvider } from '@/assets/svgs/icon-ds-service-provider.svg';
import { ReactComponent as IconClient } from '@/assets/svgs/icon-ds-user-group.svg';
import { ReactComponent as IconHome } from '@/assets/svgs/icon-home.svg';
import { ReactComponent as IconUseCase } from '@/assets/svgs/icon-use-cases.svg';
import { ReactComponent as IconPitStop } from '@/assets/svgs/pitstop.svg';

import { useNavigate, useLocation } from '@/lib/router-dom';
import { useTradex } from '@/hooks/use-tradex';
import { useDsvAdmin } from '@/hooks/use-dsv-admin/index';
import { useFeature } from '@/hooks/use-feature/index';
import { getDexConfig } from '@/utils/get-dex-config';
import { getEnvAndDex } from '@/utils/get-env-and-dex';
import { featureConstant } from '@/constants/configuration/feature.js';
import { Separator } from '@dextech-ai/dex-design-system';

const STATUS_NOT_ENROLLED = 'NOT_ENROLLED';
const USER_TYPES = {
	participant: 'participant',
	admin: 'admin',
	nonParticipant: 'nonParticipant',
};
const USER_ROLES = {
	SUPER_ADMIN: 'Super Admin',
	ADMIN: 'Admin User',
	TECH_USER: 'Tech User',
};

const MENU_IDS = [
	'/home',
	'/use-cases',
	'/dashboard',
	'/onboarding/my-data-exchange',
	'/onboarding/my-service-provider',
	'/onboarding/my-client',
	'/user-management',
	'/my-activities',
	'/pitstop',
	'/admin/participants',
	'/admin/approval-requests',
	'/data-dictionary',
	'/dsv',
];

const RELATIONSHIPS_SUB_IDS = [
	'/onboarding/my-data-exchange',
	'/onboarding/my-service-provider',
	'/onboarding/my-client',
];

const resolveActiveItemId = (currentPath) => {
	if (!currentPath) return undefined;
	return MENU_IDS
		.filter((id) => currentPath === id || currentPath.startsWith(`${id}/`))
		.sort((a, b) => b.length - a.length)[0];
};

/**
 * Icon wrapper. Forwards `...rest` (className, aria-*, etc.) to the raw
 * SVG so Antd's `MenuItem` can attach `.ant-menu-item-icon` via
 * `cloneElement` and its collapsed-mode label-hide rule
 * (`.ant-menu-item-icon + span { opacity: 0 }`) actually matches. Without
 * the spread, Antd's className stops at this helper and labels leak into
 * the 80 px collapsed rail.
 */
const Icon = ({ Glyph, ...rest }) => (
	<Glyph width={20} height={20} style={{ marginInlineEnd: 16, flexShrink: 0 }} {...rest} />
);

/**
 * Layout overrides scoped to AppSidebar. Injected via `GlobalStyles` so
 * the legacy layout SCSS (`_layoutDS.scss`, `layouts/_layout-default.scss`)
 * stays untouched: the 240 px aside/content/sticky-bar assumption baked
 * in for the legacy `Navigation` still applies on any route that doesn't
 * render `<AppSidebar>` (e.g. shell-hosted `PortalNav`). When AppSidebar
 * mounts, these rules take over and the aside column / content offset /
 * sticky-actions bar track the DS Sidebar's own responsive widths
 * (drawer 0 / collapsed 80 / expanded 260 at md=900, lg=1200 - matches
 * `useSidebarLayout` in the DS).
 */
const LAYOUT_OVERRIDES = {
	'.DS__layout-left__side-nav': { width: '0 !important' },
	'.DS__layout-right__content': { width: '100% !important', marginLeft: '0 !important' },
	'.layout-default__sticky-actions': { width: '100% !important', marginLeft: '0 !important' },
	'@media (min-width: 900px)': {
		'.DS__layout-left__side-nav': { width: '80px !important' },
		'.DS__layout-right__content': { width: 'calc(100% - 80px) !important', marginLeft: '80px !important' },
		'.layout-default__sticky-actions': { width: 'calc(100% - 80px) !important', marginLeft: '80px !important' },
	},
	'@media (min-width: 1200px)': {
		'.DS__layout-left__side-nav': { width: '260px !important' },
		'.DS__layout-right__content': { width: 'calc(100% - 260px) !important', marginLeft: '260px !important' },
		'.layout-default__sticky-actions': { width: 'calc(100% - 260px) !important', marginLeft: '260px !important' },
	},
};

const SIDEBAR_SX = {
	// DS Sidebar's `<nav>` is content-height by default; stretch it to the
	// aside's full 100vh so the sidebar background reaches the bottom of
	// the viewport like the legacy Navigation did.
	'& > nav': {
		minHeight: '100vh',
	},
	// SVGs in menu items have hardcoded `fill` on their paths, so they
	// don't inherit `color` by default. On hover/selected/active states the
	// row's text colour is already primary-main via DS Sidebar rules; force
	// SVG paths to adopt `currentColor` in those states so the icon tints too.
	'& .ant-menu-item:hover svg *, & .ant-menu-item-active svg *, & .ant-menu-item-selected svg *, & .ant-menu-submenu-title:hover svg *, & .ant-menu-submenu-active > .ant-menu-submenu-title svg *, & .ant-menu-submenu-open > .ant-menu-submenu-title svg *': {
		fill: 'currentColor',
	},
};

const AppSidebar = () => {
	const { goTo } = useNavigate();
	const { currentPath } = useLocation();

	const user = useSelector((state) => state.auth.user);
	const userType = useSelector((state) => state.auth.userType);
	const profile = useSelector((state) => state.auth.profile);

	const { dex } = getEnvAndDex();
	const { LogoComponent } = getDexConfig(dex);
	const isTradex = useTradex();
	const isDSVEnabled = useFeature(featureConstant.DSV_ENABLED);
	const isDsvAdmin = useDsvAdmin();

	const activeItemId = useMemo(() => resolveActiveItemId(currentPath), [currentPath]);

	const defaultOpenItemIds = useMemo(
		() => (RELATIONSHIPS_SUB_IDS.some((id) => activeItemId === id) ? ['relationships'] : []),
		[activeItemId],
	);

	const isEnrolled = profile?.organization?.status !== STATUS_NOT_ENROLLED;
	const isSuperAdmin = user?.role?.some((role) => role.name === USER_ROLES.SUPER_ADMIN);
	const isAdmin = user?.role?.some((role) => role.name === USER_ROLES.ADMIN);
	const isTechUser = user?.role?.some((role) => role.name === USER_ROLES.TECH_USER);

	return (
		<>
		<GlobalStyles styles={LAYOUT_OVERRIDES} />
		<Box sx={SIDEBAR_SX}>
		<Sidebar
			activeItemId={activeItemId}
			defaultOpenItemIds={defaultOpenItemIds}
			onItemSelect={(id) => goTo(id)}
		>
			<SidebarHeader>
				<Box display="flex" align="center" direction="row" gap={3} pt={10} pr={14} pb={6} pl={5}>
					<LogoComponent height={30} />
					<Separator orientation="vertical" height={18} />
					<span data-ds-text="body-sm"> Admin</span>
				</Box>
			</SidebarHeader>
			<SidebarContent>
				<SidebarMenu>
					{userType === USER_TYPES.nonParticipant && (
						<>
							<SidebarMenuItem>
								<SidebarMenuButton id="/home" icon={<Icon Glyph={IconHome} />}>Home</SidebarMenuButton>
							</SidebarMenuItem>
							<SidebarMenuItem>
								<SidebarMenuButton id="/use-cases" icon={<Icon Glyph={IconUseCase} />}>Use Cases</SidebarMenuButton>
							</SidebarMenuItem>
						</>
					)}

					{userType === USER_TYPES.participant && isEnrolled && (
						<>
							{isTradex && (
								<SidebarMenuItem>
									<SidebarMenuButton id="/dashboard" icon={<Icon Glyph={IconDashboard} />}>Dashboard</SidebarMenuButton>
								</SidebarMenuItem>
							)}
							{isTradex && (isSuperAdmin || isAdmin) && (
								<SidebarMenuItem>
									<SidebarMenuButton id="/use-cases" icon={<Icon Glyph={IconUseCase} />}>Use Cases</SidebarMenuButton>
								</SidebarMenuItem>
							)}

							{!isTradex && (
								<SidebarMenuItem>
									<SidebarMenuSub
										id="relationships"
										label="Relationships Setup"
										icon={<Icon Glyph={IconRelationShipSetup} />}
									>
										<SidebarMenuSubItem>
											<SidebarMenuButton id="/onboarding/my-data-exchange" icon={<Icon Glyph={IconDataExchange} />}>
												My Data Exchange
											</SidebarMenuButton>
										</SidebarMenuSubItem>
										<SidebarMenuSubItem>
											<SidebarMenuButton id="/onboarding/my-service-provider" icon={<Icon Glyph={IconServiceProvider} />}>
												My Solution Provider
											</SidebarMenuButton>
										</SidebarMenuSubItem>
										<SidebarMenuSubItem>
											<SidebarMenuButton id="/onboarding/my-client" icon={<Icon Glyph={IconClient} />}>
												My Client
											</SidebarMenuButton>
										</SidebarMenuSubItem>
									</SidebarMenuSub>
								</SidebarMenuItem>
							)}

							{isSuperAdmin && (
								<SidebarMenuItem>
									<SidebarMenuButton id="/user-management" icon={<Icon Glyph={IconUserManagement} />}>
										User Management
									</SidebarMenuButton>
								</SidebarMenuItem>
							)}
							{(isSuperAdmin || isTechUser) && (
								<>
									<SidebarMenuItem>
										<SidebarMenuButton id="/my-activities" icon={<Icon Glyph={IconActivity} />}>Activity Log</SidebarMenuButton>
									</SidebarMenuItem>
									<SidebarMenuItem>
										<SidebarMenuButton id="/pitstop" icon={<Icon Glyph={IconPitStop} />}>Pitstops</SidebarMenuButton>
									</SidebarMenuItem>
								</>
							)}
						</>
					)}

					{userType === USER_TYPES.admin && (
						<>
							<SidebarMenuItem>
								<SidebarMenuButton id="/admin/participants" icon={<Icon Glyph={IconParticipants} />}>Participants</SidebarMenuButton>
							</SidebarMenuItem>
							<SidebarMenuItem>
								<SidebarMenuButton id="/admin/approval-requests" icon={<Icon Glyph={IconApprovalRequests} />}>Approvals</SidebarMenuButton>
							</SidebarMenuItem>
							<SidebarMenuItem>
								<SidebarMenuButton id="/my-activities" icon={<Icon Glyph={IconActivity} />}>Activity Log</SidebarMenuButton>
							</SidebarMenuItem>
						</>
					)}

					<SidebarMenuItem>
						<SidebarMenuButton id="/data-dictionary" icon={<Icon Glyph={IconDataDictionary} />}>Data Dictionary</SidebarMenuButton>
					</SidebarMenuItem>

					{isDSVEnabled && isDsvAdmin && (
						<SidebarMenuItem>
							<SidebarMenuButton id="/dsv" icon={<Icon Glyph={IconDataExchange} />}>Data Element</SidebarMenuButton>
						</SidebarMenuItem>
					)}
				</SidebarMenu>
			</SidebarContent>
		</Sidebar>
		</Box>
		</>
	);
};

export default AppSidebar;
